/**
 * spatialUtils.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Chart2Video — canvas coordinate system utilities
 * March 2026
 *
 * PURPOSE
 * Canvas drawing happens on a grid whose dimensions are set by the active
 * ExportFormat (default 16:9 = 1920×1080; also supports 9:16, 1:1, 4:5).
 * The origin (0,0) is dead centre. This file is the single place that knows
 * how to translate between the offset-based position system used in TextElement
 * and ChartLayoutConfig, and the absolute canvas coordinates that ctx.fillText /
 * ctx.arc expect.
 *
 * Sprint D update: all coordinate functions accept canvasFormat?: ExportFormat
 * (default '16:9') so they work correctly across all four canvas sizes.
 * Use CANVAS_DIMS[canvasFormat] for raw dimensions; getCanvasDims(canvasFormat)
 * for { width, height, cx, cy }; getCanvasBoundsForFormat(canvasFormat) for
 * clamp bounds.
 *
 * COORDINATE SYSTEM — read this before using any function here
 * ─────────────────────────────────────────────────────────────────────────────
 *
 *   Canvas dimensions:   CANVAS_DIMS[canvasFormat]  (default 1920 × 1080)
 *   Origin (0, 0):       dead centre of the canvas
 *   X axis:              positive = rightward,  negative = leftward
 *   Y axis:              positive = DOWNWARD,   negative = upward  ← canvas Y is inverted
 *
 *   Absolute corners for 16:9 (1920×1080):
 *     top-left     (-960, -540)     top-right    (+960, -540)
 *     bottom-left  (-960, +540)     bottom-right (+960, +540)
 *
 *   Canvas 2D context uses a DIFFERENT origin — top-left = (0, 0) in ctx coords.
 *   The absolute coords in this file use the CENTRE-ORIGIN system for consistency
 *   with agent reasoning and SCHEMA_NOTES. The conversion to ctx coords is:
 *     ctx_x = abs_x + cx     (cx = canvas width  / 2)
 *     ctx_y = abs_y + cy     (cy = canvas height / 2)
 *
 * POSITION OFFSETS
 * TextElement.position and ChartLayoutConfig.chartPosition are NOT absolute coords.
 * They are pixel offsets added to a BASE ANCHOR. See BASE_ANCHORS below.
 * Base anchors are expressed in 16:9 space and scale proportionally for other formats.
 * Use getAbsolutePosition() to resolve an offset to absolute coords.
 * Use getCtxPosition() to get the value ready to pass to ctx.fillText() etc.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 */

import type { Position2D, CanvasZone } from '../types';
import type { ExportFormat } from '../types';
import { CANVAS_DIMS } from '../types';

// Convenience re-export — importers can use either:
//   import { CANVAS_DIMS } from '../types'   (primary, preferred)
//   import { CANVAS_DIMS } from '../utils/spatialUtils'  (convenience for files already importing here)
export { CANVAS_DIMS };



// =============================================================================
// CANVAS CONSTANTS
// =============================================================================

// 16:9 base canvas dimensions — used by validateConfig.ts for heatmap overflow checks.
// For all renderer and position math, use CANVAS_DIMS[canvasFormat] or getCanvasDims().
export const CANVAS_W  = 1920;
export const CANVAS_H  = 1080;
export const CANVAS_CX = CANVAS_W / 2;   // 960  — centre x in ctx coords
export const CANVAS_CY = CANVAS_H / 2;   // 540  — centre y in ctx coords

/** Absolute canvas bounds in centre-origin coords. */
export const CANVAS_BOUNDS = {
  minX: -CANVAS_CX,   // -960
  maxX:  CANVAS_CX,   // +960
  minY: -CANVAS_CY,   // -540
  maxY:  CANVAS_CY,   // +540
} as const;

/**
 * Returns { width, height, cx, cy } for the given ExportFormat.
 * cx/cy are the half-dimensions — the centre offsets for ctx-space conversion.
 * Used by toCtxCoords, clampToBounds, getSafeOffsetRange.
 */
export function getCanvasDims(canvasFormat: ExportFormat = '16:9') {
  const { width, height } = CANVAS_DIMS[canvasFormat];
  return { width, height, cx: width / 2, cy: height / 2 };
}

/**
 * Returns the absolute canvas bounds in centre-origin coords for the given format.
 * Used by clampToBounds and getSafeOffsetRange.
 */
export function getCanvasBoundsForFormat(canvasFormat: ExportFormat = '16:9') {
  const { width, height } = CANVAS_DIMS[canvasFormat];
  return {
    minX: -(width  / 2),
    maxX:  (width  / 2),
    minY: -(height / 2),
    maxY:  (height / 2),
  };
}


// =============================================================================
// BASE ANCHORS
// The fixed canvas position each element type starts from before its
// position offset is applied. Expressed in ABSOLUTE (centre-origin) coords.
//
// When position = { x: 0, y: 0 }, the element renders exactly at its base anchor.
// Positive x moves RIGHT. Positive y moves DOWN.
//
// KEY FOR UNDERSTANDING AGENT INSTRUCTIONS:
//   "move the title 50px to the right" → titlePosition.x += 50
//   "move the title 30px up"           → titlePosition.y -= 30
//   "place the infobox at canvas centre" → infoBoxPosition = { x: 910, y: 340 }
//     (because infoBox base anchor is at abs (-910, -340) — canvas top-left margin)
// =============================================================================

export type ElementKey =
  | 'title'          // titles[0]
  | 'title-extra'    // titles[1+]
  | 'subtitle'       // subtitles[0]
  | 'subtitle-extra' // subtitles[1+]
  | 'infoBox'        // infoBoxes[0]
  | 'infoBox-extra'  // infoBoxes[1+]
  | 'chart';         // ChartLayoutConfig.chartPosition

/**
 * Base anchors in absolute (centre-origin) canvas coordinates.
 * The base anchor is where the element renders when position offset = (0, 0).
 *
 * Index 0 of titles/subtitles/infoBoxes use the fixed anchors.
 * Index >= 1 of any type uses CANVAS_CENTRE — the user drags from there.
 */
export const BASE_ANCHORS: Record<ElementKey, Position2D> = {
  'title':          { x:    0, y: -440 },  // absolute (960, 100) in ctx — top-centre
  'title-extra':    { x:    0, y:    0 },  // canvas centre — user drags
  'subtitle':       { x:    0, y: -390 },  // absolute (960, 150) in ctx — below title
  'subtitle-extra': { x:    0, y:    0 },
  'infoBox':        { x: -910, y: -340 },  // absolute (50, 200) in ctx — top-left margin
  'infoBox-extra':  { x:    0, y:    0 },
  'chart':          { x:    0, y:    0 },  // chart body centred by default
};

/**
 * Returns the correct ElementKey for a given element type and array index.
 * Index 0 uses the standard anchor; index >= 1 uses the canvas-centre anchor.
 */
export function getElementKey(
  type: 'title' | 'subtitle' | 'infoBox' | 'chart',
  index = 0,
): ElementKey {
  if (type === 'chart') return 'chart';
  if (index === 0) return type as ElementKey;
  return `${type}-extra` as ElementKey;
}


// =============================================================================
// ANCHOR SCALING
// =============================================================================

/**
 * Scales a base anchor from the 16:9 reference frame to the active canvas format.
 * Returns the anchor unchanged for '16:9'. For other formats, scales proportionally.
 */
export function scaleAnchor(
  anchor16: Position2D,
  canvasFormat: ExportFormat = '16:9',
): Position2D {
  if (canvasFormat === '16:9') return anchor16;
  const { cx, cy } = getCanvasDims(canvasFormat);
  const REF_CX = 960;
  const REF_CY = 540;
  return {
    x: anchor16.x === 0 ? 0 : Math.round(anchor16.x * (cx / REF_CX)),
    y: anchor16.y === 0 ? 0 : Math.round(anchor16.y * (cy / REF_CY)),
  };
}


// =============================================================================
// COORDINATE CONVERSION
// =============================================================================

/**
 * Converts a position offset to ABSOLUTE canvas coordinates (centre-origin).
 * The result is in the same coord space as CANVAS_BOUNDS.
 *
 * Sprint D: base anchors are defined in 16:9 space (1920×1080).
 * For other formats, anchors scale proportionally to the canvas dimensions.
 * The infoBox x-anchor (−910 = 50px from the left edge in 16:9) becomes
 * −(cx − 50) for any format, keeping the 50px left margin semantics.
 */
export function getAbsolutePosition(
  elementType: 'title' | 'subtitle' | 'infoBox' | 'chart',
  index: number,
  offset: Position2D,
  canvasFormat: ExportFormat = '16:9',
): Position2D {
  const key    = getElementKey(elementType, index);
  const anchor = scaleAnchor(BASE_ANCHORS[key], canvasFormat);

  return {
    x: anchor.x + offset.x,
    y: anchor.y + offset.y,
  };
}

/**
 * Converts absolute (centre-origin) coords to ctx-space coords (top-left origin).
 * Pass the result directly to ctx.fillText(), ctx.arc(), ctx.translate(), etc.
 *
 * @example
 * const abs = getAbsolutePosition('title', 0, titleElement.position);
 * const ctx_pos = toCtxCoords(abs);
 * ctx.fillText(text, ctx_pos.x, ctx_pos.y);
 */
export function toCtxCoords(abs: Position2D, canvasFormat: ExportFormat = '16:9'): Position2D {
  const { cx, cy } = getCanvasDims(canvasFormat);
  return {
    x: abs.x + cx,
    y: abs.y + cy,
  };
}

/**
 * Convenience: offset → ctx coords in one call.
 * Most common usage in renderers.
 */
export function getCtxPosition(
  elementType:  'title' | 'subtitle' | 'infoBox' | 'chart',
  index:        number,
  offset:       Position2D,
  canvasFormat: ExportFormat = '16:9',
): Position2D {
  return toCtxCoords(getAbsolutePosition(elementType, index, offset, canvasFormat), canvasFormat);
}


// =============================================================================
// ZONE INFERENCE
// The canvas is divided into 9 named zones based on absolute position.
// Zone is ALWAYS computed from coordinates — never set directly.
//
// THRESHOLDS (absolute, centre-origin):
//   Horizontal thirds:  x < -320 = left,  x > +320 = right,  else centre
//   Vertical thirds:    y < -180 = top,    y > +180 = bottom, else mid
//
// These thresholds divide the canvas into equal thirds:
//   Horizontal: 640px each (960−640=320 from centre)
//   Vertical:   360px each (540−360=180 from centre)
// =============================================================================

const ZONE_X_LEFT  = -320;
const ZONE_X_RIGHT =  320;
const ZONE_Y_TOP   = -180;
const ZONE_Y_BOT   =  180;

/**
 * Returns the named zone for an absolute (centre-origin) canvas position.
 * The zone describes where the element visually lands on the canvas.
 *
 * Used by agents to describe layout in human-readable terms and to reason
 * about spatial relationships between elements.
 *
 * @param abs - absolute canvas position (centre-origin). Use getAbsolutePosition() first.
 */
export function getCanvasZone(abs: Position2D): CanvasZone {
  const col = abs.x < ZONE_X_LEFT ? 'left' : abs.x > ZONE_X_RIGHT ? 'right' : 'centre';
  const row = abs.y < ZONE_Y_TOP  ? 'top'  : abs.y > ZONE_Y_BOT   ? 'bottom': 'mid';
  if (row === 'mid' && col === 'centre') return 'centre';
  return `${row}-${col}` as CanvasZone;
}

/**
 * Describes where an element's offset will land visually.
 * Returns both the zone name and the absolute coords — useful for agent logging
 * and for SCHEMA_NOTES agentNote instructions.
 *
 * @example
 * describePosition('title', 0, { x: 0, y: 0 })
 * // → { zone: 'top-centre', absolute: { x: 0, y: -440 }, ctxCoords: { x: 960, y: 100 } }
 *
 * describePosition('infoBox', 0, { x: 0, y: 0 })
 * // → { zone: 'top-left', absolute: { x: -910, y: -340 }, ctxCoords: { x: 50, y: 200 } }
 */
export function describePosition(
  elementType: 'title' | 'subtitle' | 'infoBox' | 'chart',
  index: number,
  offset: Position2D,
): { zone: CanvasZone; absolute: Position2D; ctxCoords: Position2D } {
  const absolute  = getAbsolutePosition(elementType, index, offset);
  const ctxCoords = toCtxCoords(absolute);
  const zone      = getCanvasZone(absolute);
  return { zone, absolute, ctxCoords };
}


// =============================================================================
// BOUNDS CHECKING + CLAMPING
// =============================================================================

/**
 * Checks whether an absolute (centre-origin) position is fully within canvas bounds,
 * accounting for the element's pixel dimensions.
 *
 * @param abs       - absolute canvas position of the element's anchor point
 * @param elementW  - element width in pixels (optional — just checks anchor if omitted)
 * @param elementH  - element height in pixels (optional)
 * @param padding   - extra margin from canvas edge. Default: 20px.
 */
export function isInBounds(
  abs: Position2D,
  elementW = 0,
  elementH = 0,
  padding  = 20,
): boolean {
  const halfW = elementW / 2;
  const halfH = elementH / 2;
  return (
    abs.x - halfW >= CANVAS_BOUNDS.minX + padding &&
    abs.x + halfW <= CANVAS_BOUNDS.maxX - padding &&
    abs.y - halfH >= CANVAS_BOUNDS.minY + padding &&
    abs.y + halfH <= CANVAS_BOUNDS.maxY - padding
  );
}

/**
 * Clamps a position OFFSET so that the resulting absolute position stays
 * within canvas bounds. Returns the clamped offset (not the absolute position).
 *
 * Used by drag handlers in CanvasPositionOverlay — prevents users from dragging
 * elements off-canvas.
 *
 * @param elementType - element type (determines base anchor)
 * @param index       - array index
 * @param offset      - the proposed offset to clamp
 * @param elementW    - element width for edge detection (optional)
 * @param elementH    - element height for edge detection (optional)
 * @param padding     - edge margin. Default: 20px.
 */
export function clampToBounds(
  elementType:  'title' | 'subtitle' | 'infoBox' | 'chart',
  index:        number,
  offset:       Position2D,
  elementW    = 0,
  elementH    = 0,
  padding     = 20,
  canvasFormat: ExportFormat = '16:9',
): Position2D {
  const key    = getElementKey(elementType, index);
  const anchor = scaleAnchor(BASE_ANCHORS[key], canvasFormat);
  const halfW  = elementW / 2;
  const halfH  = elementH / 2;
  const bounds = getCanvasBoundsForFormat(canvasFormat);

  const clampedX = Math.max(
    bounds.minX + halfW + padding - anchor.x,
    Math.min(
      bounds.maxX - halfW - padding - anchor.x,
      offset.x,
    ),
  );

  const clampedY = Math.max(
    bounds.minY + halfH + padding - anchor.y,
    Math.min(
      bounds.maxY - halfH - padding - anchor.y,
      offset.y,
    ),
  );

  return { x: clampedX, y: clampedY };
}


// =============================================================================
// OFFSET ↔ ABSOLUTE HELPERS FOR AGENT USE
// =============================================================================

/**
 * Converts a desired ABSOLUTE position to the offset that produces it.
 * Useful when an agent wants to place an element at a specific absolute location
 * and needs to know what offset value to set.
 *
 * @example
 * // Agent wants the title to appear at absolute (0, -300) — high on the canvas
 * absoluteToOffset('title', 0, { x: 0, y: -300 })
 * // → { x: 0, y: 140 }   (because base anchor is at y=-440, so offset = -300 - (-440) = 140)
 */
export function absoluteToOffset(
  elementType: 'title' | 'subtitle' | 'infoBox' | 'chart',
  index:       number,
  desiredAbs:  Position2D,
  canvasFormat: ExportFormat = '16:9',
): Position2D {
  const key    = getElementKey(elementType, index);
  const anchor = scaleAnchor(BASE_ANCHORS[key], canvasFormat);
  return {
    x: desiredAbs.x - anchor.x,
    y: desiredAbs.y - anchor.y,
  };
}

/**
 * Returns a human-readable description of the safe offset range for an element.
 * Agents can call this before setting a position to understand the valid range.
 *
 * @example
 * getSafeOffsetRange('title', 0)
 * // → {
 * //     xMin: -960, xMax: 960,   // full canvas width
 * //     yMin: -100, yMax: 980,   // from near top-edge to near bottom-edge
 * //     note: "Title base anchor is at absolute (0, -440). Offset (0,0) = top-centre."
 * //   }
 */
export function getSafeOffsetRange(
  elementType:  'title' | 'subtitle' | 'infoBox' | 'chart',
  index:        number,
  padding     = 20,
  canvasFormat: ExportFormat = '16:9',
): { xMin: number; xMax: number; yMin: number; yMax: number; note: string } {
  const key    = getElementKey(elementType, index);
  const anchor = scaleAnchor(BASE_ANCHORS[key], canvasFormat);
  const bounds = getCanvasBoundsForFormat(canvasFormat);
  return {
    xMin: bounds.minX + padding - anchor.x,
    xMax: bounds.maxX - padding - anchor.x,
    yMin: bounds.minY + padding - anchor.y,
    yMax: bounds.maxY - padding - anchor.y,
    note: `${elementType}[${index}] base anchor is at absolute (${anchor.x}, ${anchor.y}). ` +
          `Offset (0,0) = ${getCanvasZone(anchor)} zone. Positive x = rightward. Positive y = downward.`,
  };
}